import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Observable, Subject } from 'rxjs';
import { AccountNotification } from '@smmof/entity';
import { Store } from '@ngrx/store';
import { selectAllAccountNotifications, selectUnreadCount } from '../../../store/selectors/notifications.selectors';
import { AccountNotificationsActions } from 'app/store/actions/notifications.actions';

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
  @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

  notifications$: Observable<AccountNotification[]>;
  unreadCount$: Observable<number>;
  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private store: Store
  ) {
    // Подписываемся на селекторы
    this.notifications$ = this.store.select(selectAllAccountNotifications);
    this.unreadCount$ = this.store.select(selectUnreadCount);
  }

  ngOnInit(): void {
    // Загрузка уведомлений при инициализации компонента
    this.store.dispatch(AccountNotificationsActions.loadNotifications());

    // Подключение к WebSocket при инициализации
    this.store.dispatch(AccountNotificationsActions.connectWebSocket());
  }

  ngOnDestroy(): void {
    // Завершаем подписки
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Уничтожаем overlay, если он существует
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  // Открыть панель уведомлений
  openPanel(): void {
    if (!this._notificationsPanel || !this._notificationsOrigin) {
      return;
    }

    if (!this._overlayRef) {
      this._createOverlay();
    }

    // Прикрепляем портал к overlay
    this._overlayRef.attach(
      new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
    );
  }

  // Закрыть панель уведомлений
  closePanel(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
    }
  }

  // Пометить все уведомления как прочитанные
  markAllAsRead(): void {
    this.store.dispatch(AccountNotificationsActions.markAllAsRead());
  }

  // Переключить статус прочтения уведомления
  toggleRead(notification: AccountNotification): void {
    const updatedNotification: AccountNotification = {
      ...notification,
      readAt: new Date(),
    };

    this.store.dispatch(
      AccountNotificationsActions.updateNotification({ notification: updatedNotification })
    );
  }

  // Удалить уведомление
  delete(notification: AccountNotification): void {
    this.store.dispatch(
      AccountNotificationsActions.deleteNotification({ id: notification.id })
    );
  }

  // TrackBy функция для ngFor
  trackByFn(index: number, item: AccountNotification): any {
    return item.id || index;
  }

  // Создание overlay
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._notificationsOrigin._elementRef)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    // Закрываем overlay при клике на задний фон
    this._overlayRef.backdropClick().subscribe(() => {
      this.closePanel();
    });
  }
}
